import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { getSessionId, streamSSE } from "@/lib/stream";
import { LogoMark } from "./Logo";
import { Reveal, Eyebrow } from "./Reveal";

const CardBack = () => (
  <div className="arch-round absolute inset-0 flex items-center justify-center overflow-hidden border border-[#CBA365]/60 bg-[#1A0A2E] [backface-visibility:hidden]">
    <div className="arch-round absolute inset-3 border border-[#CBA365]/30" />
    <div className="absolute h-40 w-40 rounded-full bg-[#6D28D9]/40 blur-3xl" />
    <LogoMark className="relative h-20 w-20 text-[#CBA365]" />
  </div>
);

const CardFront = ({ card }) => (
  <div className="arch-round absolute inset-0 flex flex-col items-center justify-center overflow-hidden border border-[#CBA365] bg-gradient-to-b from-[#2a1248] to-[#0d0519] text-center [backface-visibility:hidden] [transform:rotateY(180deg)]">
    <div className="arch-round absolute inset-3 border border-[#CBA365]/40" />
    <p className="font-display text-lg tracking-[0.4em] text-[#CBA365]">{card?.number}</p>
    <Sparkles className="my-6 h-10 w-10 text-[#CBA365]" />
    <p data-testid="daily-card-name" className="font-display px-4 text-3xl italic text-[#F5F2EB]">{card?.name}</p>
  </div>
);

export const DailyCard = ({ onBook }) => {
  const [card, setCard] = useState(null);
  const [text, setText] = useState("");
  const [state, setState] = useState("idle");

  const reveal = async () => {
    if (state !== "idle") return;
    setState("loading");
    await streamSSE("/ai/daily-card", { session_id: getSessionId() }, (ev) => {
      if (ev?.type === "card") setCard(ev.card);
      if (ev?.type === "delta") setText((t) => t + (ev.text ?? ""));
      if (ev?.type === "done") setState("done");
      if (ev?.type === "error") { setText(ev.text ?? ""); setState("error"); }
    });
  };

  return (
    <section id="carta-do-dia" data-testid="daily-card-section" className="relative mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <button data-testid="daily-card-reveal-btn" onClick={reveal} aria-label="Revelar carta do dia"
            className="group mx-auto block [perspective:1200px]">
            <motion.div animate={{ rotateY: card ? 180 : 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              whileHover={card ? {} : { y: -8, rotate: -2 }}
              className="relative aspect-[2/3] w-[58vw] max-w-[300px] [transform-style:preserve-3d] sm:w-[280px]">
              <CardBack />
              <CardFront card={card} />
            </motion.div>
          </button>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <Eyebrow>Carta do dia</Eyebrow>
            <h2 className="font-display mt-6 text-5xl leading-[0.95] tracking-tight text-[#F5F2EB] md:text-6xl">
              Uma carta <em className="gold-text">escolheu você hoje.</em>
            </h2>
          </Reveal>
          <div data-testid="daily-card-message" className="mt-8 min-h-[140px] max-w-lg text-base leading-relaxed text-white/80 md:text-lg">
            {state === "idle" && <p className="text-white/70">Respire fundo, pense no seu dia e toque na carta para revelá-la. Uma por dia.</p>}
            {state === "loading" && !text && <p className="animate-pulse text-[#CBA365]">Embaralhando os arcanos…</p>}
            {text && <p className={`font-display text-2xl italic leading-snug md:text-[1.7rem] ${state === "error" ? "text-red-200" : "text-[#F5F2EB]"}`}>{text}</p>}
          </div>
          {state === "done" && (
            <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="daily-card-book-btn" onClick={() => onBook()}
              className="mt-8 rounded-full bg-[#CBA365] px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#080312] transition-colors duration-300 hover:bg-[#E5C085]">
              Aprofundar numa leitura
            </motion.button>
          )}
          {state === "idle" && (
            <button onClick={reveal} data-testid="daily-card-cta" className="mt-8 rounded-full border border-[#CBA365] px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#CBA365] transition-colors duration-300 hover:bg-[#CBA365] hover:text-[#080312]">
              Revelar minha carta
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
